import { action, computed, observable } from 'mobx'
import type {
  NumericAnswer,
  NumericQuestion,
} from '@/features/jobs/questions/questions'
import { numberToLocaleString, roundToPrecision } from '@/utils/number-util'
import { isNotNil } from '@/utils/object-util'
import { QuestionAnswerEditorViewModelBase } from '@/features/jobs/questions/QuestionAnswerEditorViewModelBase'
import { QuestionValidationResult } from '@/features/jobs/questions/question-validation-result'

/**
 * Question answer editor for `Numeric` questions.
 */
export class NumericQuestionAnswerEditorViewModel extends QuestionAnswerEditorViewModelBase<NumericQuestion> {
  /** @inheritDoc */
  readonly type = 'Numeric'

  @observable accessor value: number | null = null
  @observable accessor decimalPlaces = 0

  /** @inheritDoc */
  @computed get isAnswered(): boolean {
    return this.value !== null
  }

  /** @inheritDoc */
  @computed get answer(): NumericAnswer | null {
    return this.value === null ? null : { type: 'Numeric', value: this.value }
  }

  /** @inheritDoc */
  set(question: NumericQuestion) {
    this.decimalPlaces =
      question.format.type === 'Decimal' ? question.format.places : 0
    this.value = question.answer
      ? roundToPrecision(question.answer.value, this.decimalPlaces)
      : null
  }

  /**
   * Responds to the question.
   *
   * @param value
   */
  @action.bound
  respond(value: number | null | undefined) {
    this.value = isNotNil(value) ? value : null
    this.notifyAnswerChanged()
  }

  /** @inheritDoc */
  validate(): QuestionValidationResult {
    if (this.value === null || Number.isNaN(this.value)) {
      return QuestionValidationResult.failed(['Please enter a number.'])
    }

    return QuestionValidationResult.ok()
  }

  /** @inheritDoc */
  protected formatTextAnswer(): string {
    return this.value === null
      ? ''
      : numberToLocaleString(this.value, this.decimalPlaces)
  }
}
